/**
 * Opens and closes the PDP size chart overlay (drawer or modal).
 * Loads content by fetching the picked page URL and injecting #MainContent.
 * Removes Theme Editor orphans on document.body and syncs display style from the open button.
 */
(() => {
  const MODAL_ID = 'pdp-size-chart-modal';
  const BACKDROP_ID = 'pdp-size-chart-backdrop';
  const OPEN_SELECTOR = '[data-pdp-size-chart-open]';
  const CLOSE_SELECTOR = '[data-pdp-size-chart-close]';
  const CONTENT_SELECTOR = '[data-size-chart-content]';
  const DRAWER_CLASS = 'pdp-size-chart--drawer';
  const MODAL_CLASS = 'pdp-size-chart--modal';

  /** @type {Map<string, string>} */
  const contentCache = new Map();

  let scrollLockY = 0;
  let scrollLocked = false;

  /**
   * Blocks scrolling of the page behind the overlay.
   * Does not lock scrolling inside the size guide modal panel.
   * @param {WheelEvent | TouchEvent} event
   */
  const blockBackgroundScroll = (event) => {
    if (!scrollLocked) return;

    const target = event.target instanceof Element ? event.target : null;
    if (target?.closest('.pdp-size-chart__panel')) return;

    event.preventDefault();
  };

  /**
   * Stops the page behind the overlay from scrolling on mobile, tablet, and desktop.
   */
  const lockPageScroll = () => {
    if (scrollLocked) return;

    scrollLockY = window.scrollY || document.documentElement.scrollTop || 0;
    scrollLocked = true;

    document.documentElement.setAttribute('scroll-lock', '');
    document.documentElement.classList.add('pdp-size-chart-open');
    document.body.classList.add('pdp-size-chart-open');
    document.documentElement.style.setProperty('overflow', 'hidden', 'important');
    document.body.style.setProperty('position', 'fixed', 'important');
    document.body.style.setProperty('top', `-${scrollLockY}px`, 'important');
    document.body.style.setProperty('left', '0', 'important');
    document.body.style.setProperty('right', '0', 'important');
    document.body.style.setProperty('width', '100%', 'important');
    document.body.style.setProperty('overflow', 'hidden', 'important');

    document.addEventListener('wheel', blockBackgroundScroll, { capture: true, passive: false });
    document.addEventListener('touchmove', blockBackgroundScroll, { capture: true, passive: false });
  };

  /**
   * Restores page scroll after the overlay closes.
   */
  const unlockPageScroll = () => {
    if (!scrollLocked) return;

    scrollLocked = false;
    document.documentElement.removeAttribute('scroll-lock');
    document.documentElement.classList.remove('pdp-size-chart-open');
    document.body.classList.remove('pdp-size-chart-open');
    document.documentElement.style.removeProperty('overflow');
    document.body.style.removeProperty('position');
    document.body.style.removeProperty('top');
    document.body.style.removeProperty('left');
    document.body.style.removeProperty('right');
    document.body.style.removeProperty('width');
    document.body.style.removeProperty('overflow');

    document.removeEventListener('wheel', blockBackgroundScroll, { capture: true });
    document.removeEventListener('touchmove', blockBackgroundScroll, { capture: true });
    window.scrollTo(0, scrollLockY);
  };

  /**
   * Removes duplicate overlay nodes so getElementById always targets the latest render.
   * Prefer keeping nodes still inside a section; drop body orphans when a fresh copy exists.
   * @param {string} id
   * @returns {HTMLElement | null}
   */
  const resolveUniqueById = (id) => {
    const matches = Array.from(document.querySelectorAll(`[id="${id}"]`));
    if (matches.length === 0) return null;
    if (matches.length === 1) return matches[0];

    const inSection = matches.filter((el) => el.closest('.shopify-section'));
    const keep = inSection[inSection.length - 1] || matches[matches.length - 1];

    matches.forEach((el) => {
      if (el !== keep) el.remove();
    });

    return keep;
  };

  /**
   * Applies drawer or modal classes/attributes onto overlay nodes.
   * @param {HTMLElement} modal
   * @param {HTMLElement} backdrop
   * @param {string} style
   */
  const applySizeChartStyle = (modal, backdrop, style) => {
    const nextStyle = style === 'modal' ? 'modal' : 'drawer';

    [modal, backdrop].forEach((el) => {
      el.dataset.sizeChartStyle = nextStyle;
      el.classList.toggle(DRAWER_CLASS, nextStyle === 'drawer');
      el.classList.toggle(MODAL_CLASS, nextStyle === 'modal');
    });
  };

  /**
   * Moves overlay elements to the document body so they stack above the sticky header.
   * @returns {{ modal: HTMLElement, backdrop: HTMLElement } | null}
   */
  const mountToBody = () => {
    const modal = resolveUniqueById(MODAL_ID);
    const backdrop = resolveUniqueById(BACKDROP_ID);

    if (!modal || !backdrop) return null;

    if (modal.parentElement !== document.body) {
      document.body.append(modal);
    }

    if (backdrop.parentElement !== document.body) {
      document.body.append(backdrop);
    }

    return { modal, backdrop };
  };

  /**
   * @returns {{ modal: HTMLElement, backdrop: HTMLElement } | null}
   */
  const getElements = () => mountToBody();

  /**
   * Wraps each table in a horizontal scroll container so wide charts stay readable.
   * @param {HTMLElement} root
   */
  const wrapTablesForScroll = (root) => {
    root.querySelectorAll('table').forEach((table) => {
      if (table.parentElement?.classList.contains('pdp-size-chart__table-scroll')) return;

      const wrapper = document.createElement('div');
      wrapper.className = 'pdp-size-chart__table-scroll';
      table.replaceWith(wrapper);
      wrapper.append(table);
    });
  };

  /**
   * Removes Size Guide hero banners from overlay content (keep live page unchanged).
   * @param {HTMLElement} root
   */
  const stripHeroBanners = (root) => {
    root.querySelectorAll('.full-width-banner').forEach((banner) => {
      const section = banner.closest('.shopify-section');
      (section || banner).remove();
    });
  };

  /**
   * Force compact padding on fetched sections (inline --er-pad-* vars otherwise win).
   * @param {HTMLElement} root
   */
  const tightenSectionPadding = (root) => {
    root.querySelectorAll('.editorial-row').forEach((el) => {
      if (!(el instanceof HTMLElement)) return;
      el.style.setProperty('padding', '8px 12px 12px', 'important');
      el.style.setProperty('margin', '0', 'important');
      [
        '--er-pad-t-m',
        '--er-pad-r-m',
        '--er-pad-b-m',
        '--er-pad-l-m',
        '--er-pad-t-d',
        '--er-pad-r-d',
        '--er-pad-b-d',
        '--er-pad-l-d',
      ].forEach((prop) => el.style.setProperty(prop, '0px'));
    });

    root.querySelectorAll('.shipping-delivery-tables').forEach((el) => {
      if (!(el instanceof HTMLElement)) return;
      el.style.setProperty('padding', '8px 0', 'important');
      el.style.setProperty('margin', '0', 'important');
    });
  };

  /**
   * Applies fetched markup, strips hero, wraps tables, and returns HTML for caching.
   * @param {HTMLElement} content
   * @param {string} markup
   * @returns {string}
   */
  const applySizeChartMarkup = (content, markup) => {
    content.innerHTML = markup;
    stripHeroBanners(content);
    tightenSectionPadding(content);
    wrapTablesForScroll(content);
    return content.innerHTML;
  };

  /**
   * Fetches the size guide page and injects #MainContent into the modal.
   * @param {HTMLElement} modal
   * @returns {Promise<void>}
   */
  const loadSizeChartContent = async (modal) => {
    const content = modal.querySelector(CONTENT_SELECTOR);
    if (!(content instanceof HTMLElement)) return;

    const url = content.getAttribute('data-size-chart-url');
    if (!url) {
      content.innerHTML = '<p class="pdp-size-chart__error">Size guide page is unavailable.</p>';
      return;
    }

    if (content.dataset.sizeChartLoaded === url && contentCache.has(url)) {
      content.innerHTML = contentCache.get(url) || '';
      return;
    }

    if (contentCache.has(url)) {
      content.innerHTML = contentCache.get(url) || '';
      content.dataset.sizeChartLoaded = url;
      return;
    }

    content.innerHTML = '<p class="pdp-size-chart__loading">Loading…</p>';

    try {
      const response = await fetch(url, { credentials: 'same-origin' });
      if (!response.ok) {
        throw new Error(`Failed to load size guide (${response.status})`);
      }

      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const main = doc.querySelector('#MainContent') || doc.querySelector('main');

      if (!main) {
        throw new Error('Size guide content not found');
      }

      const prepared = applySizeChartMarkup(content, main.innerHTML);
      contentCache.set(url, prepared);
      content.dataset.sizeChartLoaded = url;
    } catch (_error) {
      content.innerHTML =
        '<p class="pdp-size-chart__error">Unable to load size guide. Please try again.</p>';
      delete content.dataset.sizeChartLoaded;
    }
  };

  /**
   * @param {string} [styleFromTrigger]
   */
  const openSizeChart = (styleFromTrigger) => {
    const elements = getElements();
    if (!elements) return;

    if (styleFromTrigger) {
      applySizeChartStyle(elements.modal, elements.backdrop, styleFromTrigger);
    }

    elements.modal.hidden = false;
    elements.backdrop.hidden = false;
    lockPageScroll();

    loadSizeChartContent(elements.modal);
  };

  const closeSizeChart = () => {
    const elements = getElements();
    unlockPageScroll();
    if (!elements) return;

    elements.modal.hidden = true;
    elements.backdrop.hidden = true;
  };

  /**
   * Drops body-mounted overlays so the next section render can remount cleanly.
   */
  const discardBodyOverlays = () => {
    closeSizeChart();

    document.querySelectorAll(`#${MODAL_ID}, #${BACKDROP_ID}`).forEach((el) => {
      if (el.parentElement === document.body) {
        el.remove();
      }
    });
  };

  document.addEventListener('click', (event) => {
    const target = /** @type {HTMLElement} */ (event.target);

    const openTrigger = target.closest(OPEN_SELECTOR);
    if (openTrigger) {
      event.preventDefault();
      const style = openTrigger.getAttribute('data-size-chart-style') || 'drawer';
      openSizeChart(style);
      return;
    }

    if (target.closest(CLOSE_SELECTOR)) {
      event.preventDefault();
      closeSizeChart();
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;

    const elements = getElements();
    if (!elements || elements.modal.hidden) return;

    closeSizeChart();
  });

  document.addEventListener('shopify:section:unload', discardBodyOverlays);
  document.addEventListener('shopify:section:load', () => {
    mountToBody();
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', mountToBody, { once: true });
  } else {
    mountToBody();
  }
})();
